import { FaCheckCircle, FaTimes } from 'react-icons/fa';
import styled, { keyframes } from 'styled-components';
import { useEffect, useState, useRef } from 'react';
import Confetti from 'react-confetti';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  0% { transform: scale(0); }
  70% { transform: scale(1.2); }
  100% { transform: scale(1); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 1rem;
`;

const Modal = styled.div`
  background-color: white;
  border-radius: ${props => props.inline ? '8px' : '12px'};
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  position: relative;
  box-shadow: ${props => props.inline ? '0 5px 15px rgba(0, 0, 0, 0.1)' : '0 10px 25px rgba(0, 0, 0, 0.2)'};
  text-align: center;
  animation: ${slideUp} 0.3s ease forwards;

  @media (max-width: 768px) {
    padding: 1.5rem;
    max-width: 90%;
  }
`;

const InlineContainer = styled.div`
  width: 100%;
  height: 100%;
  animation: ${fadeIn} 0.3s ease forwards;
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  border-radius: 0; /* Remove border radius for full card appearance */
  box-shadow: none; /* Remove shadow when inside card */
  background-color: white;
  position: relative; /* Added for positioning the confetti properly */
  overflow: hidden; /* Keeps confetti within container */
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #777;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
    color: #333;
  }
`;

const Icon = styled.div`
  font-size: 3.5rem;
  color: #4CAF50;
  margin-bottom: 1rem;
  animation: ${scaleIn} 0.5s ease-out;
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const Title = styled.h2`
  color: #f57c00;
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Message = styled.p`
  color: #555;
  font-size: 1.05rem;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const NextStepsSection = styled.div`
  text-align: left;
  margin-bottom: 1.5rem;
`;

const NextStepsHeader = styled.h4`
  color: #333;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const NextStepsList = styled.ol`
  padding-left: 1.5rem;
  color: #555;
`;

const NextStepsItem = styled.li`
  margin-bottom: 0.5rem;
  line-height: 1.5;
`;

const CloseButtonLarge = styled.button`
  background-color: #f57c00;
  color: white;
  border: none;
  border-radius: 6px;
  padding: ${props => props.inline ? '0.7rem 1.5rem' : '0.8rem 2rem'};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #e65100;
  }
`;

const ThankYouModal = ({ isOpen, onClose, userInfo, inline = false }) => {
  // Initialize confetti to show immediately
  const [showConfetti, setShowConfetti] = useState(true);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({
    width: 500, // Set default initial width
    height: 600  // Set default initial height
  });

  // Just handle the confetti timeout - no delay in showing it
  useEffect(() => {
    if (isOpen) {
      // Only handle the timeout to hide confetti
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
    // Reset confetti state when modal reopens
    return () => setShowConfetti(true);
  }, [isOpen]);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
      
      // Update container dimensions for inline mode
      if (containerRef.current && inline) {
        setContainerSize({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight
        });
      }
    };

    // Run immediately 
    handleResize();
    
    window.addEventListener('resize', handleResize);
    
    // Also run after a very short delay to ensure DOM has rendered
    const timer = setTimeout(handleResize, 10);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, [inline]);

  if (!isOpen) return null;

  const content = (
    <>
      {!inline && (
        <CloseButton onClick={onClose}>
          <FaTimes />
        </CloseButton>
      )}
      
      <Icon>
        <FaCheckCircle />
      </Icon>
      
      <Title>Thank You{userInfo?.name ? `, ${userInfo.name.split(' ')[0]}` : ''}!</Title>
      
      <Message>
        Your enquiry has been successfully submitted. One of our travel experts will review your request and contact you shortly.
      </Message>
      
      <NextStepsSection>
        <NextStepsHeader>What happens next?</NextStepsHeader>
        <NextStepsList>
          <NextStepsItem>Our travel expert will review your request details</NextStepsItem>
          <NextStepsItem>You'll receive a call within 24 hours to discuss your requirements</NextStepsItem>
          <NextStepsItem>We'll create a customized itinerary based on your preferences</NextStepsItem>
        </NextStepsList>
      </NextStepsSection>
      
      <CloseButtonLarge onClick={onClose}>
        Close
      </CloseButtonLarge>
    </>
  );

  return inline ? (
    <InlineContainer ref={containerRef}>
      {showConfetti && (
        <Confetti
          width={containerSize.width || 500}
          height={containerSize.height || 600}
          recycle={false}
          numberOfPieces={200}
          gravity={0.3} // Slightly faster falling confetti
          confettiSource={{
            x: containerSize.width / 2,
            y: 0,
            w: 0,
            h: 0
          }}
        />
      )}
      {content}
    </InlineContainer>
  ) : (
    <ModalOverlay>
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={500}
          gravity={0.3} // Slightly faster falling confetti
        />
      )}
      <Modal inline={inline}>
        {content}
      </Modal>
    </ModalOverlay>
  );
};

export default ThankYouModal;
